import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useReso } from '../contexts/ResoContext';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';
import Loader from '../components/common/loader';

const AuthSuccess: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshSession: refreshResoSession } = useReso();
  const { getConfigValue, tenant, isAddEditHost, initializeTenant } =
    useTenant();
  const { session } = useAuth();
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    const handleAuthSuccess = async () => {
      try {
        // For addedit hosts, ensure we have the correct tenant from session
        if (isAddEditHost() && session?.user?.tenant) {
          // Re-initialize tenant if needed (in case auth-success was incorrectly used as tenant)
          await initializeTenant();
        }

        // Now refresh RESO session with the correct tenant config
        await refreshResoSession(getConfigValue('RESO_BASE_URL'));

        // Extract the next parameter with a more reliable method
        const fullSearch = location.search;

        // Remove the leading '?' if present
        const searchWithoutQuestion = fullSearch.startsWith('?')
          ? fullSearch.substring(1)
          : fullSearch;

        // Split by 'next=' to get everything after it
        const parts = searchWithoutQuestion.split('next=');

        if (parts.length > 1) {
          // This is everything after 'next=' - the complete URL
          const encodedNext = parts[1];
          const next = decodeURIComponent(encodedNext);

          try {
            // Extract just the path and query part from the full URL
            const urlObj = new URL(next);
            let targetPath = urlObj.pathname + urlObj.search;

            // For addedit hosts, ensure the path includes the tenant
            if (isAddEditHost() && session?.user?.tenant) {
              const tenantPrefix = `/${session.user.tenant}`;

              // If the target path doesn't start with the tenant, add it
              if (!targetPath.startsWith(tenantPrefix)) {
                // Remove leading slash from targetPath if present
                const pathWithoutLeadingSlash = targetPath.startsWith('/')
                  ? targetPath.substring(1)
                  : targetPath;

                // Construct the correct path
                targetPath = `${tenantPrefix}/${pathWithoutLeadingSlash}`;
              }
            }

            navigate(targetPath);
          } catch (e) {
            console.error('Invalid next URL:', next, e);

            // Navigate to default route based on domain type
            if (isAddEditHost() && session?.user?.tenant) {
              navigate(`/${session.user.tenant}/create`);
            } else {
              navigate('/create');
            }
          }
        } else {
          // No next parameter, go to default route
          if (isAddEditHost() && session?.user?.tenant) {
            navigate(`/${session.user.tenant}/create`);
          } else {
            navigate('/create');
          }
        }
      } catch (error) {
        console.error('Failed to process auth success:', error);
        setIsProcessing(false);
      }
    };

    handleAuthSuccess();
  }, [
    location,
    navigate,
    refreshResoSession,
    getConfigValue,
    isAddEditHost,
    session,
    tenant,
    initializeTenant,
  ]);

  return (
    <Loader showHeaderFooter={false} text={'Completing authentication...'} />
  );
};

export default AuthSuccess;
